/**
 * Custom Hook for Fetching Conversation Messages from API
 * Uses React Query with no caching
 */

import { useQuery } from '@tanstack/react-query';
import { conversationHistoryService } from '@/services/chatHistoryService';
import { ConversationMessagesResponse } from '@/types/chatTypes';

interface UseConversationMessagesReturn {
  messages: ConversationMessagesResponse | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Custom hook for fetching conversation messages from the API using React Query
 * Configured with no caching (gcTime: 0, staleTime: 0)
 * @param user_id - The user ID (optional when fetching by conversationId)
 * @param session_id - The session ID (conversation ID) or conversationId when useConversationIdEndpoint is true
 * @param useConversationIdEndpoint - If true, uses /conversations/{conversationId}?useRedis=true endpoint instead of /conversations/messages/{user_id}/{session_id}
 * @returns Conversation messages state and refetch function
 */
export const useConversationMessages = (
  user_id: string | null,
  session_id: string | null,
  useConversationIdEndpoint: boolean = false
): UseConversationMessagesReturn => {
  const {
    data: messages,
    isLoading,
    error,
    refetch,
  } = useQuery<ConversationMessagesResponse>({
    queryKey: [
      'conversationMessages',
      user_id,
      session_id,
      useConversationIdEndpoint,
    ],
    queryFn: async () => {
      if (useConversationIdEndpoint) {
        // Use conversationId endpoint for shared URLs
        if (!session_id) {
          throw new Error('Conversation ID is required');
        }
        return conversationHistoryService.fetchConversationById(session_id);
      } else {
        // Use traditional endpoint with user_id and session_id
        if (!user_id || !session_id) {
          throw new Error('User ID and Session ID are required');
        }
        return conversationHistoryService.fetchConversationMessages({
          user_id,
          session_id,
        });
      }
    },
    enabled: useConversationIdEndpoint
      ? !!session_id // Only need session_id when using conversationId endpoint
      : !!user_id && !!session_id, // Need both when using traditional endpoint
    gcTime: 0, // No cache time - data is immediately garbage collected
    staleTime: 0, // Data is immediately considered stale - always refetch
    retry: 1, // Retry once on failure
  });

  return {
    messages: messages || null,
    isLoading,
    error: error
      ? error instanceof Error
        ? error.message
        : 'Failed to fetch conversation messages'
      : null,
    refetch,
  };
};
